import { type Control, type UseFormWatch, useController } from 'react-hook-form';
import { Box, Column, Link, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { UserProfession } from '~/enums/UserProfession';
import { getIsAllowOfficeToPostProcedure } from '~/utils/DentistUtils';
import { ReadOnlyField } from '../EditShift/ReadOnlyField';
import { AssistedHygieneField } from '../ShiftDetails/ShiftDetailsForm/fields/AssistedHygieneField';
import { ExpectedProceduresField } from '../ShiftDetails/ShiftDetailsForm/fields/ExpectedProcedures';
import { MealBreakDurationField } from '../ShiftDetails/ShiftDetailsForm/fields/MealBreakDurationField';
import { MealBreakPaidField } from '../ShiftDetails/ShiftDetailsForm/fields/MealBreakPaidField';
import { ProfessionalPayField } from '../ShiftDetails/ShiftDetailsForm/fields/ProfessionalPayField';
import { TimeFieldsWrapper } from '../ShiftDetails/ShiftDetailsForm/fields/TimeFieldsWrapper';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { type IEditOpenShiftFormValues } from './useEditOpenShift';

interface IEditOpenShiftFormProps {
  control: Control<IEditOpenShiftFormValues>;
  watch: UseFormWatch<IEditOpenShiftFormValues>;
  officeName: string;
  /** Office that owns this shift — drives the rate recommendation. */
  officeId: string;
  /** Onsite-interview only: title of the associated job posting. */
  postingTitle?: string;
  /** Onsite-interview only: ID of the associated job posting for the link. */
  postingId?: string;
}

export const EditOpenShiftForm = ({
  control,
  watch,
  officeName,
  officeId,
  postingTitle,
  postingId,
}: IEditOpenShiftFormProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const professionCode = professionType ? UserProfession.getCode(professionType) : '';
  const isRDH = professionCode === UserProfession.RDH;
  const showProcedures = getIsAllowOfficeToPostProcedure(professionType, preferredSpecialty);

  const shiftControl = control as unknown as Control<IShiftDetailsFormValues>;

  const { field: isAssistedHygieneField } = useController({ name: 'isAssistedHygiene', control });
  const { field: arrivalField } = useController({ name: 'arrivalTime', control });
  const { field: departureField } = useController({ name: 'departureTime', control });
  const { field: mealBreakDurationField } = useController({ name: 'mealBreakDuration', control });
  const { field: isMealBreakPaidField } = useController({ name: 'isMealBreakPaid', control });
  const { field: rateField } = useController({ name: 'rate', control });

  return (
    <Column data-testid="edit-open-shift-form" data-cy="edit-open-shift-form" gap={5}>
      <Column style={{ gap: 6 }}>
        <Typography as="span" bold variant="body1">
          Office:
        </Typography>
        <Typography as="span" numberOfLines={2}>
          {officeName}
        </Typography>
      </Column>

      {postingTitle && postingId && (
        <Column gap={1} data-testid="job-title-field" data-cy="job-title-field">
          <Typography as="span" bold variant="body1">
            Job Title:
          </Typography>
          <Link href={`/permanent-jobs/job-overview/${postingId}`} underline="always">
            {postingTitle}
          </Link>
        </Column>
      )}

      <ReadOnlyField
        label="Professional Type:"
        suffix="(Read-Only)"
        value={professionType}
        testId="profession-type-read-only"
      />

      <ReadOnlyField
        label="Preferred Specialty:"
        suffix="(Read-Only)"
        value={preferredSpecialty?.replace('specialty_', '') || ''}
        testId="preferred-specialty-read-only"
      />

      {showProcedures && (
        <>
          <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
          <ExpectedProceduresField
            control={shiftControl}
            professionType={professionType}
            preferredSpecialty={preferredSpecialty}
          />
          <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
        </>
      )}

      {isRDH && (
        <AssistedHygieneField
          value={isAssistedHygieneField.value}
          onChange={isAssistedHygieneField.onChange}
        />
      )}

      <Column gap={3} style={{ marginTop: 16 }}>
        <Typography as="span" bold size="heading_3" color={colors.neutral[600]}>
          Hours & Pay
        </Typography>
        <Box style={{ height: '1.5px', backgroundColor: colors.neutral[100] }} />
      </Column>

      <TimeFieldsWrapper
        arrivalValue={arrivalField.value}
        onArrivalChange={arrivalField.onChange}
        departureValue={departureField.value}
        onDepartureChange={departureField.onChange}
      />

      <Row style={{ gap: 30 }}>
        <Column style={{ flex: 1 }}>
          <MealBreakDurationField
            value={mealBreakDurationField.value}
            onChange={mealBreakDurationField.onChange}
          />
        </Column>
        <Column style={{ flex: 1 }}>
          <MealBreakPaidField
            value={isMealBreakPaidField.value}
            onChange={isMealBreakPaidField.onChange}
          />
        </Column>
      </Row>

      <ProfessionalPayField
        value={rateField.value}
        onChange={rateField.onChange}
        professionType={professionType}
        officeId={officeId}
      />
    </Column>
  );
};
